import React, { useCallback, useState } from 'react';
import { useFormContext } from 'react-hook-form';
import Button from '@material-ui/core/Button';
import Grid from '@material-ui/core/Grid';
import Alert from '@material-ui/lab/Alert';
import { EmailDetails, EmailRemindersFields } from 'features/sendouts/components/forms';
import {
  INTERVIEW_SCHEDULE_TIME_FRAME_MAP,
  InterviewSchedule
} from 'features/sendouts/components/forms/InterviewSchedule';
import { ATTACHMENTS_CATEGORIES } from 'features/sendouts/constants';
import strings from 'strings';
import { When } from 'UI/components/atoms';
import NumberedForm from 'UI/components/molecules/NumberedForm';
import { CandidateFileCategoriesWithSendout } from 'UI/components/organisms/sendouts/utils';

import { AccountableRecruiterSelector, Attachments, CandidateDetailsFields } from '../../../shared';

import { FORM_FIELDS_MAP } from './SendoutForm.constants';
import { NumberedFormWrapper } from './SendoutForm.styled';
import { checkIfCandidateIsAlreadyInSendout, mapCandidateAttachments } from './SendoutForm.utils';

export const SendoutForm = ({
  candidateInfo = null,
  jobOrder = null,
  onCandidateInfoChange = () => {},
  onDuplicateChange = () => {},
  onViewDuplicates = () => {}
}) => {
  const { watch } = useFormContext();

  const [candidateAttachments, setCandidateAttachments] = useState(null);
  const [isCandidateDuplicate, setIsCandidateDuplicate] = useState(false);

  const jobOrderId = jobOrder?.id;

  const formValues = watch([
    FORM_FIELDS_MAP.Candidate.key,
    FORM_FIELDS_MAP.CandidateRecruiter.key,
    FORM_FIELDS_MAP.HiringAuthority.key,
    FORM_FIELDS_MAP.InterviewDate.key,
    FORM_FIELDS_MAP.InterviewTimeFrame.key,
    FORM_FIELDS_MAP.InterviewTime.key,
    FORM_FIELDS_MAP.InterviewFromHour.key,
    FORM_FIELDS_MAP.InterviewTimezone.key,
    FORM_FIELDS_MAP.InterviewAddress.key,
    FORM_FIELDS_MAP.InterviewLink.key
  ]);

  const candidateValue = formValues[FORM_FIELDS_MAP.Candidate.key];
  const hiringAuthorityValue = formValues[FORM_FIELDS_MAP.HiringAuthority.key];
  const candidateRecruiterValue = formValues[FORM_FIELDS_MAP.CandidateRecruiter.key];
  const interviewDate = formValues[FORM_FIELDS_MAP.InterviewDate.key];
  const interviewLink = formValues[FORM_FIELDS_MAP.InterviewLink.key];

  const interviewTime =
    formValues[FORM_FIELDS_MAP.InterviewTimeFrame.key]?.id ===
    INTERVIEW_SCHEDULE_TIME_FRAME_MAP.FixedHour.id
      ? formValues[FORM_FIELDS_MAP.InterviewTime.key]
      : formValues[FORM_FIELDS_MAP.InterviewFromHour.key];

  const interviewTimezone = formValues[FORM_FIELDS_MAP.InterviewTimezone.key];

  const shouldDisplayReminders =
    !!formValues[FORM_FIELDS_MAP.InterviewTimeFrame.key] &&
    !!interviewTime &&
    !!interviewTimezone &&
    !!interviewDate;

  const accountableSelectorOptions = [
    {
      ...jobOrder?.recruiter,
      fullName: jobOrder?.recruiter?.personalInformation?.full_name
    }
  ]; // TODO: apply logic to get specific options according to user role

  const handleOnSelectCandidate = useCallback(
    async candidate => {
      const formattedFiles = mapCandidateAttachments(candidate?.files); // TODO: this mapping function is needed to convert the candidate files in the expected format for Attachments component. We can get rid of it when the sendouts api v2 is ready and the candidate files are coming with file_type_id field directly.
      setCandidateAttachments(formattedFiles);
      const nextCandidateInfo = {
        name: candidate?.personalInformation?.first_name,
        phone: candidate?.personalInformation?.contact?.phone
      };

      onCandidateInfoChange(nextCandidateInfo);

      const { isDuplicate, duplicates, error } = await checkIfCandidateIsAlreadyInSendout({
        candidateId: candidate?.id,
        jobOrderId
      });

      onDuplicateChange({
        isDuplicate,
        duplicates,
        error
      });

      setIsCandidateDuplicate(isDuplicate);
    },
    [jobOrderId, onCandidateInfoChange, onDuplicateChange]
  );

  return (
    <Grid container>
      <Grid item container xs={12}>
        {accountableSelectorOptions.length > 0 && (
          <AccountableRecruiterSelector options={accountableSelectorOptions} />
        )}

        <NumberedFormWrapper item xs={12}>
          <NumberedForm>
            <NumberedForm.Item title={strings.sendoutsV2.form.sections.candidateDetails.title}>
              <CandidateDetailsFields
                onSelectCandidate={handleOnSelectCandidate}
                showEstimatedFeeAmount
              />
            </NumberedForm.Item>
            <When condition={isCandidateDuplicate}>
              <NumberedForm.Container>
                <Alert
                  severity="warning"
                  action={
                    <Button
                      color="inherit"
                      size="small"
                      variant="outlined"
                      onClick={() => onViewDuplicates({ candidate: candidateValue })}
                    >
                      View
                    </Button>
                  }
                >
                  {strings.sendoutsV2.form.sections.candidateDetails.alerts.duplicateCandidate}
                </Alert>
              </NumberedForm.Container>
            </When>
            <When condition={candidateValue && candidateRecruiterValue}>
              <NumberedForm.Item title={strings.sendoutsV2.form.sections.attachments.title}>
                <Attachments
                  categories={[
                    ATTACHMENTS_CATEGORIES.resume,
                    ATTACHMENTS_CATEGORIES.portfolio,
                    ATTACHMENTS_CATEGORIES.other
                  ]}
                  categoryIdMap={CandidateFileCategoriesWithSendout}
                  files={candidateAttachments || []}
                />
              </NumberedForm.Item>
            </When>

            <When condition={candidateValue && candidateRecruiterValue}>
              <NumberedForm.Item title={strings.sendoutsV2.form.sections.interviewDetails.title}>
                <InterviewSchedule />
              </NumberedForm.Item>
            </When>
            <When condition={shouldDisplayReminders}>
              <NumberedForm.Item title={strings.sendoutsV2.form.sections.emailReminders.title}>
                <EmailRemindersFields
                  hiringAuthorityName={hiringAuthorityValue?.full_name}
                  candidateName={candidateValue?.fullName}
                  url={interviewLink}
                />
              </NumberedForm.Item>
            </When>
            <When condition={candidateValue && candidateRecruiterValue}>
              <NumberedForm.Item title={strings.sendoutsV2.form.sections.emailDetails.title}>
                <EmailDetails jobOrder={jobOrder} candidateInfo={candidateInfo} />
              </NumberedForm.Item>
            </When>
          </NumberedForm>
        </NumberedFormWrapper>
      </Grid>
    </Grid>
  );
};
